// The Search page's small rules (desktop and phone), with unit tests: the short tag in the square, the date words,
// the switches, the phone's groups, and the recent searches kept on this device.
import type { SearchKind, SearchResult } from '@dpw/shared';
import { KIND, kindOf } from '../kb/model';
import { localDate, localDay, relDay, shortDay } from '../lib/format';

/** The switches, in the prototype's order, with the words on them. */
export const KINDS: readonly (readonly [SearchKind | 'all', string])[] = [
  ['all', 'All'], ['activity', 'Activity'], ['track', 'Track'], ['minutes', 'Minutes'], ['email', 'Email'], ['file', 'File'], ['person', 'Person'], ['insight', 'Insight'],
];

/** The short tag in the square: ACT, TRK, MIN, EML, the file's type (PDF, DOC...), or a person's initials. */
export function abbrOf(r: Pick<SearchResult, 'kind' | 'title'>): string {
  switch (r.kind) {
    case 'activity': return 'ACT';
    case 'track': return 'TRK';
    case 'minutes': return 'MIN';
    case 'email': return 'EML';
    case 'file': return KIND[kindOf(r.title)][0];
    case 'insight': return 'INS';
    case 'person': return r.title.replace(/^(Dr|Eng|Mr|Mrs|Ms)\.?\s+/i, '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
  }
}

/** When it is: an activity as "Sun 1 Nov" (or Today, Tomorrow), anything else "27 Oct" or Today; people have none. */
export function whenText(r: Pick<SearchResult, 'kind' | 'when'>, today = localDate()): string {
  if (!r.when) return '';
  const d = localDay(r.when);
  if (r.kind === 'activity') return relDay(d, today);
  return d === today ? 'Today' : shortDay(d);
}

/** "#steering #governance" */
export const tagsText = (tags: string[]) => tags.map(t => `#${t}`).join(' ');

/** The phone lists results in groups: meetings (and their minutes) apart from other activities. */
export type PhoneGroup = 'Activities' | 'Meetings' | 'Tracks' | 'Emails' | 'Files' | 'People' | 'Insights';
const GROUP_ORDER: PhoneGroup[] = ['Activities', 'Meetings', 'Tracks', 'Emails', 'Files', 'Insights', 'People'];
export function phoneGroup(r: Pick<SearchResult, 'kind' | 'activityType'>): PhoneGroup {
  if (r.kind === 'minutes' || (r.kind === 'activity' && (r.activityType === 'meeting' || r.activityType === 'workshop'))) return 'Meetings';
  return r.kind === 'activity' ? 'Activities' : r.kind === 'track' ? 'Tracks' : r.kind === 'email' ? 'Emails' : r.kind === 'file' ? 'Files' : r.kind === 'insight' ? 'Insights' : 'People';
}
export function phoneGroups<T extends Pick<SearchResult, 'kind' | 'activityType'>>(items: T[]): { group: PhoneGroup; items: T[] }[] {
  return GROUP_ORDER.map(group => ({ group, items: items.filter(i => phoneGroup(i) === group) })).filter(g => g.items.length);
}

/** Recent searches on this device (a convenience: lost in a private window, never needed). Newest first, at most 6. */
const RECENT_KEY = 'dpw.search.recent';
export function recentSearches(): string[] {
  try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown; return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 6) : []; } catch { return []; }
}
export function rememberSearch(q: string): void {
  const t = q.trim();
  if (t.length < 2) return;
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([t, ...recentSearches().filter(x => x.toLowerCase() !== t.toLowerCase())].slice(0, 6))); } catch { /* storage off: nothing to keep */ }
}

/** The search modal's groups, in this order (Kareem, 4 Nov): what the person can see, every kind in one box. */
export type ModalGroup = 'Spaces' | 'Tracks' | 'Activities' | 'Meetings' | 'Emails' | 'Reminders' | 'Files and knowledge base' | 'Research insights' | 'People';
export const MODAL_ORDER: readonly ModalGroup[] = ['Spaces', 'Tracks', 'Activities', 'Meetings', 'Emails', 'Reminders', 'Files and knowledge base', 'Research insights', 'People'];
/** At most this many rows per group in the modal; "See all results" opens the Search page for the rest. */
export const MODAL_PER_GROUP = 5;

/** Which modal group a search result goes in: meetings, workshops and minutes together, files with folders. */
export function modalGroup(r: Pick<SearchResult, 'kind' | 'activityType'>): ModalGroup {
  switch (r.kind) {
    case 'activity': return r.activityType === 'meeting' || r.activityType === 'workshop' ? 'Meetings' : 'Activities';
    case 'minutes': return 'Meetings';
    case 'track': return 'Tracks';
    case 'email': return 'Emails';
    case 'file': return 'Files and knowledge base';
    case 'insight': return 'Research insights';
    case 'person': return 'People';
  }
}

/** Does every word typed appear in the text (any order, any case)? Words of one letter count too, here. */
export function matchesWords(text: string, q: string): boolean {
  const words = q.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  if (!words.length) return false;
  const t = text.toLowerCase();
  return words.every(w => t.includes(w));
}

/** One row in the modal: a result from the server or something the app already has (a space, a folder, a reminder). */
export interface ModalItem { key: string; group: ModalGroup; title: string; where: string; href: string; when: string | null; kind: string; activityType: string | null }

/** Put rows in their groups, in MODAL_ORDER, each cut to MODAL_PER_GROUP. Empty groups are left out. */
export function modalGroups(items: ModalItem[], per = MODAL_PER_GROUP): { group: ModalGroup; items: ModalItem[]; total: number }[] {
  return MODAL_ORDER.map(group => {
    const all = items.filter(i => i.group === group);
    return { group, items: all.slice(0, per), total: all.length };
  }).filter(g => g.items.length);
}

/** A search result as a modal row. */
export const fromResult = (r: SearchResult): ModalItem => ({
  key: `${r.kind}:${r.id}`, group: modalGroup(r), title: r.title, where: r.where, href: r.href, when: r.when, kind: r.kind, activityType: r.activityType,
});
